// Shared admin chrome for Circles, Networks, and Spaces.
//
// The outer component dispatches on `kind` to a kind-specific inner that
// runs its own detail + join-requests fetches and projects them onto the
// uniform `{name, archived, pendingCount}` shape the shared body consumes.
// The shared body owns the sidebar config and breadcrumb.
//
// While the detail query is in flight, the breadcrumb renders a Skeleton
// so the chrome paints immediately and the user can navigate sections
// without a blank page.

import type { ReactNode } from 'react';
import { Skeleton, type SidebarItem } from '@leanin/ui';
import { useQuery } from '@tanstack/react-query';
import { groupKeys } from '@/lib/queries/groups';
import { spacesKeys } from '@/lib/queries/spaces';
import { AdminShell } from '../../admin/AdminShell';
import {
  SECTIONS_BY_KIND,
  type GroupAdminKind,
  type GroupAdminSection,
} from './sections';

export type { GroupAdminKind, GroupAdminSection } from './sections';

type SidebarSectionId = Exclude<GroupAdminSection, 'overview'>;

type State = {
  readonly name: string | null;
  readonly archived: boolean;
  readonly pendingCount: number;
};

const KIND_CONFIG: Record<
  GroupAdminKind,
  {
    readonly overviewLabel: string;
    readonly back: (id: string) => string;
    readonly base: (id: string) => string;
  }
> = {
  circle: {
    overviewLabel: 'Circle overview',
    back: (id) => `/circles/${id}`,
    base: (id) => `/circles/${id}/admin`,
  },
  network: {
    overviewLabel: 'Network overview',
    back: (id) => `/networks/${id}`,
    base: (id) => `/networks/${id}/admin`,
  },
  space: {
    overviewLabel: 'Group overview',
    back: (id) => `/groups/${id}`,
    base: (id) => `/groups/${id}/admin`,
  },
};

// Sidebar items per kind = the full section set minus 'overview' (which
// renders as the implicit landing item).
const SIDEBAR_SECTIONS: Record<GroupAdminKind, ReadonlyArray<SidebarSectionId>> = {
  circle: SECTIONS_BY_KIND.circle.filter((s) => s !== 'overview') as ReadonlyArray<SidebarSectionId>,
  network: SECTIONS_BY_KIND.network.filter((s) => s !== 'overview') as ReadonlyArray<SidebarSectionId>,
  space: SECTIONS_BY_KIND.space.filter((s) => s !== 'overview') as ReadonlyArray<SidebarSectionId>,
};

const SIDEBAR_ITEM_DEFS: Record<
  SidebarSectionId,
  (base: string, archived: boolean, pendingCount: number) => SidebarItem
> = {
  visibility: (base) => ({ id: 'visibility', label: 'Visibility & access', icon: 'lock', href: `${base}?section=visibility` }),
  requests: (base, _archived, pendingCount) => ({
    id: 'requests', label: 'Pending requests', icon: 'users', href: `${base}?section=requests`,
    ...(pendingCount > 0 ? { badge: pendingCount } : {}),
  }),
  members: (base) => ({ id: 'members', label: 'Members', icon: 'users', href: `${base}?section=members` }),
  circles: (base) => ({ id: 'circles', label: 'Circles', icon: 'circles', href: `${base}?section=circles` }),
  broadcasts: (base) => ({ id: 'broadcasts', label: 'Broadcasts', icon: 'email', href: `${base}?section=broadcasts` }),
  archive: (base, archived) => ({
    id: 'archive', label: archived ? 'Restore' : 'Archive',
    icon: archived ? 'spark' : 'trash', href: `${base}?section=archive`,
  }),
};

type Props = {
  readonly id: string;
  readonly kind: GroupAdminKind;
  readonly section: GroupAdminSection;
  readonly children: ReactNode;
};

export function GroupAdminShell({ id, kind, section, children }: Props) {
  if (kind === 'circle') return <CircleInner id={id} section={section}>{children}</CircleInner>;
  if (kind === 'network') return <NetworkInner id={id} section={section}>{children}</NetworkInner>;
  return <SpaceInner id={id} section={section}>{children}</SpaceInner>;
}

type InnerProps = Omit<Props, 'kind'>;

function CircleInner({ id, section, children }: InnerProps) {
  const detail = useQuery(groupKeys.circle.detail(id)).data;
  const requests = useQuery(groupKeys.circle.joinRequests(id)).data;
  const circle = detail?.access === 'visitor' ? null : detail?.circle ?? null;
  return (
    <Body kind="circle" id={id} section={section} state={{
      name: circle?.name ?? null,
      archived: circle?.status === 'archived',
      pendingCount: (requests?.items ?? []).filter((r) => r.status === 'pending').length,
    }}>{children}</Body>
  );
}

function NetworkInner({ id, section, children }: InnerProps) {
  const network = useQuery(groupKeys.network.detail(id)).data;
  const requests = useQuery(groupKeys.network.joinRequests(id)).data;
  return (
    <Body kind="network" id={id} section={section} state={{
      name: network?.name ?? null,
      archived: network?.status === 'archived',
      pendingCount: (requests?.items ?? []).filter((r) => r.status === 'pending').length,
    }}>{children}</Body>
  );
}

function SpaceInner({ id, section, children }: InnerProps) {
  const space = useQuery(spacesKeys.detail(id)).data;
  // Spaces don't expose a join-requests admin section yet.
  return (
    <Body kind="space" id={id} section={section} state={{
      name: space?.name ?? null, archived: space?.archivedAt != null, pendingCount: 0,
    }}>{children}</Body>
  );
}

function Body({
  kind, id, section, state, children,
}: {
  readonly kind: GroupAdminKind;
  readonly id: string;
  readonly section: GroupAdminSection;
  readonly state: State;
  readonly children: ReactNode;
}) {
  const config = KIND_CONFIG[kind];
  const base = config.base(id);
  const items: SidebarItem[] = [
    { id: 'overview', label: config.overviewLabel, icon: 'spark', href: base },
    ...SIDEBAR_SECTIONS[kind].map((s) => SIDEBAR_ITEM_DEFS[s](base, state.archived, state.pendingCount)),
  ];
  const groupName: ReactNode = state.name !== null ? state.name : <Skeleton width={140} height={14} rounded="sm" />;
  return (
    <AdminShell backHref={config.back(id)} backLabel={groupName} items={items} active={section}>
      {children}
    </AdminShell>
  );
}
